/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { Input, Checkbox, message, Button } from 'antd';
import md5 from 'md5';
import TitleBanner from '../../components/TitleBanner';
import { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH, PDF_DOWNLOAD_PRICE_USD, TERMS_AND_CONDITIONS_URL } from '../../utils/helper';
import { PayButton, Separator, SubmitButton } from './styles';
import { InputLabel, InputContainer, SignUpPromptStyles } from '../../components/Modals/styles';
import PricingPlan from '../../components/PricingPlan';
import { preventAction } from './Tools';
import { emailExists } from '../../utils/services/authAPI';
import { signUp } from 'supertokens-web-js/recipe/emailpassword';
import { sendVerificationEmail } from 'supertokens-web-js/recipe/emailverification';
import { Icon } from 'antd';
import PasswordStrengthBar from '../../components/PasswordStrengthBar/PasswordStrengthBar';
import { CREATE_ACCOUNT } from './translationValues';
import Regex from '../../utils/regex';
import { SERVICE_MESSAGES } from '../../utils/translationValues';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import PDFDownloadIcon from './../../images/pdf-download-icon.png';

const NAMESPACES = ['CREATE_ACCOUNT', 'SERVICE_MESSAGES'];

const SignUpModal = props => {
  const { setCurrentPage, t, i18n, onSignUpComplete, showPayBtn = false, handlePayDownload, payBtnLoading } = props;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedPricingPlan, setSelectedPricingPlan] = useState();
  const [repeatPassword, setRepeatPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checked, setChecked] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [planType, setPlanType] = useState('free');
  const [loading, setLoading] = useState(false);
  const [PasswordScore, setPasswordScore] = useState('');
  const [checkBoxStatus, setCheckBoxStatus] = useState(false);

  // terms and condition checkbox
  const onChange = e => {
    setChecked(e.target.checked);
    setCheckBoxStatus(!e.target.checked);
  };

  // new and confirm password validation
  const passwordValidation = (password, confirmPassword) => {
    if (password === confirmPassword) {
      setRepeatPassword(false);
    } else {
      setRepeatPassword(true);
    }
  };

  // get score form new password
  const onChangeScore = value => {
    setPassword(value.password);
    setPasswordScore(value.score)
    if (value.score >= 3) {
      setShowPassword(true);
    }

    if (confirmPassword.length === 0) return;
    passwordValidation(value.password, confirmPassword)
  };

  // set confirm password
  const getConfirmPassword = e => {
    const value = e.target.value;
    setConfirmPassword(value);
    passwordValidation(password, value)
  };

  // email validation
  const checkEmail = async () => {
    if (email.trim().length === 0) return;

    const isValidEmail = Regex.EMAIL.test(email);
    if (!isValidEmail) {
      setEmailError(true);
      return (true);
    } else {
      setEmailError(false);
      const result = await emailExists(email);
      if (result) {
        message.warning(t(CREATE_ACCOUNT.EMAIL_EXISTS_ERROR_MESSAGE.key, CREATE_ACCOUNT.EMAIL_EXISTS_ERROR_MESSAGE.value));
        return (true);
      }
    }
  };

  const emailVerification = async () => {
    try {
      let response = await sendVerificationEmail();
      if (response.status === 'EMAIL_ALREADY_VERIFIED_ERROR') {
        // This can happen if the info about email verification in the session was outdated.
        // Redirect the user to the home page
      } else {
        // email was sent successfully.
        // message.error('Please check your email and click the link in it');
      }
    } catch (err) {
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        message.error(err.message);
      } else {
        message.error(t(CREATE_ACCOUNT.GENERAL_ERROR_MESSAGE.key, CREATE_ACCOUNT.GENERAL_ERROR_MESSAGE.value));
      }
    }
  };

  const handleSignup = async () => {
    if (loading || disabled) return;

    const response = await checkEmail(email);
    if (response) return;

    if (PasswordScore < 3) {
      message.warning(t(CREATE_ACCOUNT.WEAK_PASSWORD_ERROR_MESSAGE.key, CREATE_ACCOUNT.WEAK_PASSWORD_ERROR_MESSAGE.value));
      return;
    }
    if (repeatPassword) {
      return;
    }

    if (!checked) {
      setCheckBoxStatus(true);
      return;
    }

    setDisabled(true);
    setLoading(true);
    onSubmit();
  }

  // sign up
  const onSubmit = async () => {

    console.log("document.rewardful in SignUp onSubmit :: ", document.rewardful()?.affiliate);

    const resetButtonState = () => {
      setDisabled(false);
      setLoading(false);
    }

    const payload = {
      formFields: [
        {
          id: 'email',
          value: email,
        },
        {
          id: 'password',
          value: md5(password),
        },
        {
          id: 'planId',
          value: selectedPricingPlan,
        },
        {
          id: 'languageCode',
          value: i18n.language,
        }
      ]
    }

    if (document.rewardful) {
      const rewardful = document.rewardful();
      if (rewardful.affiliate) {
        payload.formFields.push({
          id: 'referral',
          value: rewardful.affiliate?.token
        })
      }
      if (rewardful.coupon) {
        payload.formFields.push({
          id: 'coupon',
          value: rewardful.coupon?.id
        })
      }
    }

    console.log({ payload });

    try {
      let response = await signUp(payload);

      if (response.status !== 'OK') {
        resetButtonState();
      }

      if (response.status === 'FIELD_ERROR') {
        // one of the input formFields failed validaiton
        response.formFields.forEach(formField => {
          if (formField.id === 'email') {
            // Email validation failed (for example incorrect email syntax),
            // or the email is not unique.
            message.error(formField.error);
          } else if (formField.id === 'password') {
            // Password validation failed.
            // Maybe it didn't match the password strength
            message.error(formField.error);
          }
        });
      } else if (response.status === 'GENERAL_ERROR') {
        message.error(t(`SERVICE_MESSAGES:${SERVICE_MESSAGES.GENERAL_TRY_AGAIN_ERROR.key}`, SERVICE_MESSAGES.GENERAL_TRY_AGAIN_ERROR.value));
      } else {
        if (onSignUpComplete) {
          onSignUpComplete();
        }
        // sign up successful. The session tokens are automatically handled by
        // the frontend SDK.
        await emailVerification();
      }
    } catch (err) {
      resetButtonState();
      if (err.isSuperTokensGeneralError === true) {
        message.error(err.message);
      } else {
        message.error(t(CREATE_ACCOUNT.GENERAL_ERROR_MESSAGE.key, CREATE_ACCOUNT.GENERAL_ERROR_MESSAGE.value));
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      !disabled && handleSignup();
    }
  }

  // form button validation
  useEffect(() => {
    if (email.length !== 0 && password.trim().length !== 0 && confirmPassword.trim().length !== 0) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [email, password, confirmPassword, repeatPassword]);

  // get planId from url
  useEffect(() => {
    const search = window.location.search;
    const query = new URLSearchParams(search);
    const plan = query.get('priceId');
    if (plan) {
      setPlanType(plan);
    }
  }, []);

  //  reset all state when we leave the page
  useEffect(() => {
    return () => {
      setEmail('');
      setConfirmPassword('');
      setShowPassword(false);
      setEmailError(false);
      setRepeatPassword(false);
    };
  }, []);


  return (
    <SignUpPromptStyles>
      <div className="right-side" style={ showPayBtn ? { width: '450px', padding: '48px 24px' } : {}}>
        <TitleBanner title={t(CREATE_ACCOUNT.CREATE_ACCOUNT_TITLE.key, CREATE_ACCOUNT.CREATE_ACCOUNT_TITLE.value)} />
        <div className="plan">
          <PricingPlan
            setSelectedPricingPlan={setSelectedPricingPlan}
            selectedPricingPlan={planType}
            key={planType}
          />
        </div>
        <p className="usage">
          {
            t(CREATE_ACCOUNT.CREATE_ACCOUNT_DESCRIPTION.key, CREATE_ACCOUNT.CREATE_ACCOUNT_DESCRIPTION.value)
          }
        </p>
        <div className="content-body">
          <form autoComplete="off" onKeyDown={e => e.stopPropagation()} onKeyPress={handleKeyDown}>
            <InputContainer>
              <Input
                name="email"
                size="large"
                placeholder={t(CREATE_ACCOUNT.CREATE_ACCOUNT_EMAIL_PLACEHOLDER.key, CREATE_ACCOUNT.CREATE_ACCOUNT_EMAIL_PLACEHOLDER.value)}
                autoComplete="off"
                value={email}
                onPaste={preventAction}
                onCopy={preventAction}
                maxLength={EMAIL_MAX_LENGTH}
                onChange={e => {
                  setEmail(e.target.value);
                }}
                onBlur={() => checkEmail()}
              />
              {emailError && email.length !== 0 && (
                <InputLabel signUp error className="bottom_top_anime">
                  {t(CREATE_ACCOUNT.INVALID_EMAIL_ERROR_MESSAGE.key, CREATE_ACCOUNT.INVALID_EMAIL_ERROR_MESSAGE.value)}
                </InputLabel>
              )}
            </InputContainer>

            <InputContainer bottom-0 signUp>
              <PasswordStrengthBar onChangeScore={onChangeScore} />
            </InputContainer>

            {email && showPassword && (
              <div className="bottom_top_anime">
                <InputContainer bottom-0 signUp>
                  <Input.Password
                    size="large"
                    placeholder={t(CREATE_ACCOUNT.CONFIRM_PASSWORD_PLACEHOLDER.key, CREATE_ACCOUNT.CONFIRM_PASSWORD_PLACEHOLDER.value)}
                    value={confirmPassword}
                    onChange={e => getConfirmPassword(e)}
                    onPaste={preventAction}
                    onCopy={preventAction}
                    maxLength={PASSWORD_MAX_LENGTH}
                    className="inputBox"
                  />
                  {repeatPassword && (
                    <InputLabel error signUp>
                      {t(CREATE_ACCOUNT.PASSWORD_MISMATCH_ERROR_MESSAGE.key, CREATE_ACCOUNT.PASSWORD_MISMATCH_ERROR_MESSAGE.value)}
                    </InputLabel>
                  )}
                </InputContainer>
              </div>
            )}
          </form>
        </div>
        <div className="footer label">
          <Checkbox checked={checked} onChange={onChange} className="agree-btn">
            <p>{t(CREATE_ACCOUNT.TERMS_AND_CONDITIONS_LABEL.key, CREATE_ACCOUNT.TERMS_AND_CONDITIONS_LABEL.value)}
              <a href={TERMS_AND_CONDITIONS_URL} target="_blank">
                {t(CREATE_ACCOUNT.TERMS_AND_CONDITIONS_LINK_TEXT.key, CREATE_ACCOUNT.TERMS_AND_CONDITIONS_LINK_TEXT.value)}
              </a>
            </p>
          </Checkbox>
          {checkBoxStatus && <div className='warning-message'> {t(CREATE_ACCOUNT.ACCEPT_TERMS_AND_CONDITIONS_ERROR_MESSAGE.key, CREATE_ACCOUNT.ACCEPT_TERMS_AND_CONDITIONS_ERROR_MESSAGE.value)}</div>}
        </div>
        <SubmitButton start disable={disabled || payBtnLoading} onClick={handleSignup}>
          <div className="wrap-1">
            <div> {t(CREATE_ACCOUNT.CREATE_ACCOUNT_BUTTON.key, CREATE_ACCOUNT.CREATE_ACCOUNT_BUTTON.value)} </div>
            {loading ? <Icon type="loading" className="loading" /> : <div className="arrow"></div>}
          </div>
          <div className="wrap-2">
            <div>{t(CREATE_ACCOUNT.CREATE_ACCOUNT_BUTTON.key, CREATE_ACCOUNT.CREATE_ACCOUNT_BUTTON.value)}</div>
            <div className="arrow"></div>
          </div>
        </SubmitButton>
        <div className="redirect-btn">
          {t(CREATE_ACCOUNT.ALREADY_HAVE_ACCOUNT_DESCRIPTION.key, CREATE_ACCOUNT.ALREADY_HAVE_ACCOUNT_DESCRIPTION.value)} ?{' '}
          <a
            onClick={() => {
              setCurrentPage('login');
            }}
          >
            {' '}
            {t(CREATE_ACCOUNT.REDIRECT_TO_USER_LOGIN_ACTION.key, CREATE_ACCOUNT.REDIRECT_TO_USER_LOGIN_ACTION.value)}{' '}
          </a>
        </div>
      </div>
      {showPayBtn &&
      <>
        <div className='vertical_line'><p>{t(CREATE_ACCOUNT.OR_TEXT.key, CREATE_ACCOUNT.OR_TEXT.value)}</p></div>
        <div className='card'>
          <img src={PDFDownloadIcon} />
            <h2>{t(CREATE_ACCOUNT.PDF_DOWNLOAD_PRICE_INFO.key, CREATE_ACCOUNT.PDF_DOWNLOAD_PRICE_INFO.value, { price: PDF_DOWNLOAD_PRICE_USD })}</h2>
            <Button onClick={handlePayDownload} disabled={payBtnLoading}>{payBtnLoading 
              ? <Icon type="loading" /> 
              : <Icon type="credit-card" />} {t(CREATE_ACCOUNT.PAY_DOWNLOAD_BUTTON_TEXT.key, CREATE_ACCOUNT.PAY_DOWNLOAD_BUTTON_TEXT.value, { price: PDF_DOWNLOAD_PRICE_USD })}</Button>
        </div>
      </>
      }
    </SignUpPromptStyles>
  );
};

export default withTranslation(NAMESPACES)(SignUpModal);
